import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Card } from '../shared/ui/card/card.tsx'
import { urls } from '../shared/urls.ts'
import { useAppModule } from '../features/appContext.ts'
import { Spinner } from '../shared/ui/spinner.tsx'
import type { UserCourse } from '../shared/api/chatApi.ts'
import { SUPPORT_MAX_URL } from '../shared/constants.ts'
import { cn } from '../shared/lib/utils.ts'

/** Варианты фона карточки курса (классы из tailwind.config → backgroundImage). */
const COURSE_CARD_GRADIENTS = [
  'bg-course-card-violet',
  'bg-course-card-blue',
  'bg-course-card-amber',
  'bg-course-card-green',
] as const

function getCourseCardGradientClass(
  index: number,
): (typeof COURSE_CARD_GRADIENTS)[number] {
  return COURSE_CARD_GRADIENTS[index % COURSE_CARD_GRADIENTS.length]
}

interface CourseCardButtonProps {
  course: UserCourse
  gradientIndex: number
  onSelect: (course: UserCourse) => void
}

function CourseCardButton({
  course,
  gradientIndex,
  onSelect,
}: CourseCardButtonProps) {
  const gradientClass = getCourseCardGradientClass(gradientIndex)
  return (
    <button
      type="button"
      onClick={() => onSelect(course)}
      className="block w-[400px] text-left transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue2 focus-visible:ring-offset-2"
    >
      <Card
        className={cn(
          'h-[200px] w-[400px] cursor-pointer overflow-hidden p-28 shadow-container transition-shadow hover:shadow-lg',
          gradientClass,
        )}
      >
        <h2 className="mb-12 text-20 font-bold text-black">{course.name}</h2>
        <p className="text-14 leading-relaxed text-gray-700 line-clamp-3">
          {course.description ?? ''}
        </p>
      </Card>
    </button>
  )
}

export function MyCoursesPage() {
  const intl = useIntl()
  const { chatsApi, appStorage, appStore } = useAppModule()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState<string | null>(null)
  const [courses, setCourses] = useState<UserCourse[]>([])
  const [coursesError, setCoursesError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([chatsApi.getAccount(), chatsApi.getUserCourses()])
      .then(([account, coursesList]) => {
        if (!cancelled) {
          setName(account.name ?? '')
          setCourses(Array.isArray(coursesList) ? coursesList : [])
          setCoursesError(null)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setCoursesError(
            e?.message ?? intl.formatMessage({ id: 'myCoursesLoadError' }),
          )
          setCourses([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [chatsApi, intl])

  const onCourseClick = (course: UserCourse) => {
    appStorage.set('currentCourse', {
      id: course.id,
      name: course.name,
      slug: course.slug,
      frontend_locale: course.frontend_locale,
    })
    appStore.syncUiLocaleFromStorage()
    navigate(urls.learningProfile())
  }

  if (loading) {
    return (
      <div className="flex h-dvh w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const displayName =
    name?.trim() || intl.formatMessage({ id: 'myCoursesDefaultStudentName' })

  return (
    <div className="relative flex size-full flex-col px-32 pt-32 pb-40">
      <div className="mb-32 flex flex-col gap-12">
        <img
          src="/images/empatika-logo-horizontal-color.svg"
          alt="Эмпатика"
          className="h-48 w-auto max-w-full object-contain object-left"
        />
        <p className="text-18 font-semibold text-gray-700 mt-16">
          <FormattedMessage
            id="myCoursesWelcome"
            values={{ name: displayName }}
          />
        </p>
      </div>

      {coursesError && (
        <p className="mb-16 text-14 text-red-600">{coursesError}</p>
      )}

      {courses.length === 0 && !coursesError ? (
        <p className="text-16 text-gray6">
          <FormattedMessage id="myCoursesNoCourses" />
        </p>
      ) : (
        <div className="flex flex-wrap gap-24">
          {courses.map((course, index) => (
            <CourseCardButton
              key={course.id}
              course={course}
              gradientIndex={index}
              onSelect={onCourseClick}
            />
          ))}
        </div>
      )}

      <p className="mt-40 text-16 text-gray6">
        <FormattedMessage id="myCoursesSupportIntro" />{' '}
        <a
          href={SUPPORT_MAX_URL}
          target="_blank"
          rel="noreferrer"
          className="underline hover:text-blue2"
        >
          <FormattedMessage id="myCoursesMax" />
        </a>
        .
      </p>
    </div>
  )
}
